import React, { useState, useEffect, useRef } from 'react';
import { Form, Button, Alert } from 'react-bootstrap';
import { serializeBetweenBounds } from '../utils/conditionalField';
import { formatCurrencyInputDisplay, normalizeCurrencyInput } from '../utils/currencyInput';

export type ConditionalFieldOption = { value: string; label: string };

export type ConditionalAvailableField = {
  id: number;
  label: string;
  name: string;
  field_type: string;
  options?: string;
  allow_multiple?: boolean;
  /** Quando o campo não tem `options` em texto (ex.: Tipo nativo), usa esta lista. */
  conditionalChoices?: ConditionalFieldOption[];
};

function optionsForListField(selectedField: ConditionalAvailableField): ConditionalFieldOption[] {
  if (selectedField.conditionalChoices?.length) {
    return selectedField.conditionalChoices;
  }
  const raw = selectedField.options
    ? selectedField.options.split(/[,\n]/).map((opt) => opt.trim()).filter((opt) => opt.length > 0)
    : [];
  return raw.map((opt) => ({ value: opt, label: opt }));
}

interface ConditionalValueInputProps {
  conditionalFieldId?: number;
  availableFields: ConditionalAvailableField[];
  value: string;
  onChange: (value: string) => void;
  /** Quando `between`, renderiza inputs De/Até e serializa {"min","max"}. */
  operator?: string;
}

function parseBetweenValueLoose(raw: string): { min: string; max: string } {
  if (!raw) return { min: '', max: '' };
  try {
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      return {
        min: parsed.min !== undefined && parsed.min !== null ? String(parsed.min) : '',
        max: parsed.max !== undefined && parsed.max !== null ? String(parsed.max) : '',
      };
    }
  } catch {
    // ignore
  }
  return { min: '', max: '' };
}

const ConditionalValueInput: React.FC<ConditionalValueInputProps> = ({
  conditionalFieldId,
  availableFields,
  value,
  onChange,
  operator,
}) => {
  const parseExternalValue = (rawValue: string): { parsedValues: string[]; parsedAllowMultiple: boolean } => {
    if (!rawValue) {
      return { parsedValues: [''], parsedAllowMultiple: false };
    }

    try {
      const parsed = JSON.parse(rawValue);
      if (Array.isArray(parsed)) {
        return {
          parsedValues: parsed.length > 0 ? parsed.map((item) => String(item)) : [''],
          parsedAllowMultiple: true,
        };
      }

      return { parsedValues: [String(parsed)], parsedAllowMultiple: false };
    } catch {
      return { parsedValues: [rawValue], parsedAllowMultiple: false };
    }
  };

  const initialParsed = parseExternalValue(value);
  const [multipleValues, setMultipleValues] = useState<string[]>(initialParsed.parsedValues);
  const [allowMultiple, setAllowMultiple] = useState(initialParsed.parsedAllowMultiple);
  const suppressNextEmitRef = useRef(false);
  const previousValueRef = useRef<string>(value);
  const previousConditionalFieldIdRef = useRef<number | undefined>(conditionalFieldId);
  const isInternalUpdateRef = useRef(false);
  const onChangeRef = useRef(onChange);
  
  // Atualizar ref quando onChange mudar
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  const selectedField = conditionalFieldId
    ? availableFields.find(f => f.id === conditionalFieldId)
    : null;

  // Parsear valor inicial e resetar quando o campo condicional mudar
  useEffect(() => {
    // Ignorar atualizações que vêm de mudanças internas
    if (isInternalUpdateRef.current) {
      isInternalUpdateRef.current = false;
      return;
    }

    // Se o campo condicional mudou, resetar completamente
    if (previousConditionalFieldIdRef.current !== conditionalFieldId) {
      previousConditionalFieldIdRef.current = conditionalFieldId;
      previousValueRef.current = value || '';
      
      const { parsedValues, parsedAllowMultiple } = parseExternalValue(value || '');
      suppressNextEmitRef.current = true;
      setMultipleValues(parsedValues);
      setAllowMultiple(parsedAllowMultiple);
      return;
    }

    // Só atualizar se o valor externo mudou
    if (value !== previousValueRef.current) {
      previousValueRef.current = value || '';
      
      const { parsedValues, parsedAllowMultiple } = parseExternalValue(value || '');
      suppressNextEmitRef.current = true;
      setMultipleValues(parsedValues);
      setAllowMultiple(parsedAllowMultiple);
    }
  }, [value, conditionalFieldId]);

  // Atualizar valor quando múltiplos valores mudarem (não usado no modo between)
  useEffect(() => {
    if (operator === 'between') {
      return;
    }
    if (suppressNextEmitRef.current) {
      suppressNextEmitRef.current = false;
      return;
    }

    const newValue = allowMultiple
      ? JSON.stringify(multipleValues.filter(v => v.trim() !== ''))
      : (multipleValues[0] || '');

    // Só chamar onChange se o valor realmente mudou
    if (newValue !== previousValueRef.current) {
      isInternalUpdateRef.current = true;
      previousValueRef.current = newValue;
      onChangeRef.current(newValue);
    }
  }, [multipleValues, allowMultiple, operator]);

  // Campos que não suportam valores condicionais
  if (selectedField && (selectedField.field_type === 'file' || selectedField.field_type === 'files')) {
    return (
      <Alert variant="info" className="mb-0">
        Campos do tipo arquivo não suportam valores condicionais. Este campo sempre será visível.
      </Alert>
    );
  }

  // Campo checkbox - apenas marcado/desmarcado
  if (selectedField?.field_type === 'check') {
    let options: string[] = [];
    if (selectedField.options) {
      try {
        const parsed = JSON.parse(selectedField.options);
        if (Array.isArray(parsed)) {
          options = parsed.map((opt) => String(opt).trim()).filter((opt) => opt.length > 0);
        } else {
          options = [String(parsed).trim()].filter((opt) => opt.length > 0);
        }
      } catch {
        options = selectedField.options
          .split(/[,\n]/)
          .map(opt => opt.trim())
          .filter(opt => opt.length > 0);
      }
    }

    if (options.length > 0) {
      if (allowMultiple) {
        return (
          <div>
            {multipleValues.map((val, index) => (
              <div key={index} className="d-flex gap-2 mb-2">
                <Form.Select
                  value={val}
                  onChange={(e) => {
                    const newValues = [...multipleValues];
                    newValues[index] = e.target.value;
                    setMultipleValues(newValues);
                  }}
                >
                  <option value="">Selecione uma opção...</option>
                  {options.map((opt, idx) => (
                    <option key={idx} value={opt}>
                      {opt}
                    </option>
                  ))}
                </Form.Select>
                {multipleValues.length > 1 && (
                  <Button
                    variant="outline-danger"
                    size="sm"
                    onClick={() => {
                      const newValues = multipleValues.filter((_, i) => i !== index);
                      setMultipleValues(newValues.length > 0 ? newValues : ['']);
                    }}
                  >
                    ×
                  </Button>
                )}
              </div>
            ))}
            <div className="d-flex gap-2 mb-2">
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={() => setMultipleValues([...multipleValues, ''])}
              >
                + Adicionar Opção
              </Button>
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={() => {
                  setAllowMultiple(false);
                  setMultipleValues([multipleValues[0] || '']);
                }}
              >
                Usar Opção Única
              </Button>
            </div>
            <Form.Text className="text-muted">
              Este campo aparecerá quando qualquer uma das opções selecionadas estiver marcada.
            </Form.Text>
          </div>
        );
      }

      return (
        <div>
          <Form.Select
            value={multipleValues[0] || ''}
            onChange={(e) => {
              setMultipleValues([e.target.value]);
            }}
          >
            <option value="">Selecione uma opção...</option>
            {options.map((opt, idx) => (
              <option key={idx} value={opt}>
                {opt}
              </option>
            ))}
          </Form.Select>
          <div className="mt-2">
            <Form.Check
              type="checkbox"
              label="Permitir múltiplas opções"
              checked={allowMultiple}
              onChange={(e) => {
                setAllowMultiple(e.target.checked);
                if (e.target.checked && multipleValues.length === 0) {
                  setMultipleValues(['']);
                }
              }}
            />
          </div>
          <Form.Text className="text-muted">
            Este campo aparecerá quando a opção selecionada estiver marcada.
          </Form.Text>
        </div>
      );
    }

    const isChecked = value === 'true' || value === '1';
    return (
      <div>
        <Form.Check
          type="radio"
          label="Marcado"
          name={`conditional_check_${conditionalFieldId}`}
          id={`conditional_check_${conditionalFieldId}_true`}
          checked={isChecked}
          onChange={() => onChange('true')}
        />
        <Form.Check
          type="radio"
          label="Desmarcado"
          name={`conditional_check_${conditionalFieldId}`}
          id={`conditional_check_${conditionalFieldId}_false`}
          checked={!isChecked}
          onChange={() => onChange('false')}
        />
        <Form.Text className="text-muted">
          Este campo aparecerá quando o checkbox estiver no estado selecionado.
        </Form.Text>
      </div>
    );
  }

  // Campo lista/combo - select com opções
  if (selectedField && (selectedField.field_type === 'list' || selectedField.field_type === 'combo')) {
    const options = optionsForListField(selectedField);

    if (allowMultiple) {
      return (
        <div>
          {multipleValues.map((val, index) => (
            <div key={index} className="d-flex gap-2 mb-2">
              <Form.Select
                value={val}
                onChange={(e) => {
                  const newValues = [...multipleValues];
                  newValues[index] = e.target.value;
                  setMultipleValues(newValues);
                }}
              >
                <option value="">Selecione...</option>
                {options.map((opt, idx) => (
                  <option key={idx} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </Form.Select>
              {multipleValues.length > 1 && (
                <Button
                  variant="outline-danger"
                  size="sm"
                  onClick={() => {
                    const newValues = multipleValues.filter((_, i) => i !== index);
                    setMultipleValues(newValues.length > 0 ? newValues : ['']);
                  }}
                >
                  ×
                </Button>
              )}
            </div>
          ))}
          <div className="d-flex gap-2 mb-2">
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={() => setMultipleValues([...multipleValues, ''])}
            >
              + Adicionar Valor
            </Button>
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={() => {
                setAllowMultiple(false);
                setMultipleValues([multipleValues[0] || '']);
              }}
            >
              Usar Valor Único
            </Button>
          </div>
          <Form.Text className="text-muted">
            Este campo aparecerá quando o campo condicional tiver qualquer um dos valores selecionados.
          </Form.Text>
        </div>
      );
    }

    return (
      <div>
        <Form.Select
          value={multipleValues[0] || ''}
          onChange={(e) => {
            setMultipleValues([e.target.value]);
          }}
        >
          <option value="">Selecione...</option>
          {options.map((opt, idx) => (
            <option key={idx} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </Form.Select>
        <div className="mt-2">
          <Form.Check
            type="checkbox"
            label="Permitir múltiplos valores"
            checked={allowMultiple}
            onChange={(e) => {
              setAllowMultiple(e.target.checked);
              if (e.target.checked && multipleValues.length === 0) {
                setMultipleValues(['']);
              }
            }}
          />
        </div>
        <Form.Text className="text-muted">
          Este campo aparecerá quando o campo condicional tiver o valor selecionado.
        </Form.Text>
      </div>
    );
  }

  // Operador Entre: dois inputs (De / Até) → {"min","max"}
  if (
    operator === 'between' &&
    selectedField &&
    ['integer', 'decimal', 'currency', 'date'].includes(selectedField.field_type)
  ) {
    const bounds = parseBetweenValueLoose(value || '');
    const isDate = selectedField.field_type === 'date';
    const isInteger = selectedField.field_type === 'integer';
    const usesBrDecimal = !isDate && !isInteger;
    const emitBounds = (min: string, max: string) => {
      const serialized = serializeBetweenBounds(min, max);
      isInternalUpdateRef.current = true;
      previousValueRef.current = serialized;
      onChange(serialized);
    };
    const onBoundChange = (side: 'min' | 'max', raw: string) => {
      if (usesBrDecimal) {
        const normalized = normalizeCurrencyInput(raw);
        if (normalized === null) return;
        emitBounds(side === 'min' ? normalized : bounds.min, side === 'max' ? normalized : bounds.max);
        return;
      }
      emitBounds(side === 'min' ? raw : bounds.min, side === 'max' ? raw : bounds.max);
    };
    return (
      <div>
        <div className="d-flex gap-2 align-items-end flex-wrap">
          <div className="flex-grow-1" style={{ minWidth: 120 }}>
            <Form.Label className="mb-1">De</Form.Label>
            <Form.Control
              type={isDate ? 'date' : isInteger ? 'number' : 'text'}
              inputMode={isDate ? undefined : 'decimal'}
              step={isInteger ? '1' : undefined}
              value={
                usesBrDecimal
                  ? formatCurrencyInputDisplay(bounds.min, 'BRL')
                  : bounds.min
              }
              onChange={(e) => onBoundChange('min', e.target.value)}
              placeholder={usesBrDecimal ? 'Ex.: 30000,01' : undefined}
            />
          </div>
          <div className="flex-grow-1" style={{ minWidth: 120 }}>
            <Form.Label className="mb-1">Até</Form.Label>
            <Form.Control
              type={isDate ? 'date' : isInteger ? 'number' : 'text'}
              inputMode={isDate ? undefined : 'decimal'}
              step={isInteger ? '1' : undefined}
              value={
                usesBrDecimal
                  ? formatCurrencyInputDisplay(bounds.max, 'BRL')
                  : bounds.max
              }
              onChange={(e) => onBoundChange('max', e.target.value)}
              placeholder={usesBrDecimal ? 'Ex.: 139999,99' : undefined}
            />
          </div>
        </div>
        <Form.Text className="text-muted">
          Este campo aparecerá quando o valor estiver entre De e Até (inclusive).
          {usesBrDecimal ? ' Use vírgula para centavos (ex.: 30000,01).' : ''}
        </Form.Text>
      </div>
    );
  }

  // Campo data
  if (selectedField?.field_type === 'date') {
    return (
      <div>
        <Form.Control
          type="date"
          value={multipleValues[0] || ''}
          onChange={(e) => {
            setMultipleValues([e.target.value]);
          }}
        />
        <Form.Text className="text-muted">
          Este campo aparecerá quando o campo condicional tiver a data selecionada.
        </Form.Text>
      </div>
    );
  }

  // Campos numéricos
  if (selectedField && ['integer', 'decimal', 'currency'].includes(selectedField.field_type)) {
    if (allowMultiple) {
      return (
        <div>
          {multipleValues.map((val, index) => (
            <div key={index} className="d-flex gap-2 mb-2">
              <Form.Control
                type="number"
                step={selectedField.field_type === 'integer' ? '1' : '0.01'}
                value={val}
                onChange={(e) => {
                  const newValues = [...multipleValues];
                  newValues[index] = e.target.value;
                  setMultipleValues(newValues);
                }}
                placeholder="Digite o valor"
              />
              {multipleValues.length > 1 && (
                <Button
                  variant="outline-danger"
                  size="sm"
                  onClick={() => {
                    const newValues = multipleValues.filter((_, i) => i !== index);
                    setMultipleValues(newValues.length > 0 ? newValues : ['']);
                  }}
                >
                  ×
                </Button>
              )}
            </div>
          ))}
          <div className="d-flex gap-2 mb-2">
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={() => setMultipleValues([...multipleValues, ''])}
            >
              + Adicionar Valor
            </Button>
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={() => {
                setAllowMultiple(false);
                setMultipleValues([multipleValues[0] || '']);
              }}
            >
              Usar Valor Único
            </Button>
          </div>
          <Form.Text className="text-muted">
            Este campo aparecerá quando o campo condicional tiver qualquer um dos valores inseridos.
          </Form.Text>
        </div>
      );
    }

    return (
      <div>
        <Form.Control
          type="number"
          step={selectedField.field_type === 'integer' ? '1' : '0.01'}
          value={multipleValues[0] || ''}
          onChange={(e) => {
            setMultipleValues([e.target.value]);
          }}
          placeholder="Digite o valor"
        />
        <div className="mt-2">
          <Form.Check
            type="checkbox"
            label="Permitir múltiplos valores"
            checked={allowMultiple}
            onChange={(e) => {
              setAllowMultiple(e.target.checked);
              if (e.target.checked && multipleValues.length === 0) {
                setMultipleValues(['']);
              }
            }}
          />
        </div>
        <Form.Text className="text-muted">
          Este campo aparecerá quando o campo condicional tiver o valor inserido.
        </Form.Text>
      </div>
    );
  }

  // Campos de texto (padrão)
  if (allowMultiple) {
    return (
      <div>
        {multipleValues.map((val, index) => (
          <div key={index} className="d-flex gap-2 mb-2">
            <Form.Control
              type="text"
              value={val}
              onChange={(e) => {
                const newValues = [...multipleValues];
                newValues[index] = e.target.value;
                setMultipleValues(newValues);
              }}
              placeholder="Digite o valor"
            />
            {multipleValues.length > 1 && (
              <Button
                variant="outline-danger"
                size="sm"
                onClick={() => {
                  const newValues = multipleValues.filter((_, i) => i !== index);
                  setMultipleValues(newValues.length > 0 ? newValues : ['']);
                }}
              >
                ×
              </Button>
            )}
          </div>
        ))}
        <div className="d-flex gap-2 mb-2">
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={() => setMultipleValues([...multipleValues, ''])}
          >
            + Adicionar Valor
          </Button>
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={() => {
              setAllowMultiple(false);
              setMultipleValues([multipleValues[0] || '']);
            }}
          >
            Usar Valor Único
          </Button>
        </div>
        <Form.Text className="text-muted">
          Este campo aparecerá quando o campo condicional tiver qualquer um dos valores inseridos.
        </Form.Text>
      </div>
    );
  }

  return (
    <div>
      <Form.Control
        type="text"
        value={multipleValues[0] || ''}
        onChange={(e) => {
          setMultipleValues([e.target.value]);
        }}
        placeholder="Digite o valor"
      />
      <div className="mt-2">
        <Form.Check
          type="checkbox"
          label="Permitir múltiplos valores"
          checked={allowMultiple}
          onChange={(e) => {
            setAllowMultiple(e.target.checked);
            if (e.target.checked && multipleValues.length === 0) {
              setMultipleValues(['']);
            }
          }}
        />
      </div>
      <Form.Text className="text-muted">
        Este campo aparecerá quando o campo condicional tiver o valor inserido. Marque "Permitir múltiplos valores" para aceitar vários valores diferentes.
      </Form.Text>
    </div>
  );
};

export default ConditionalValueInput;
